@let agents = (agentsService.agents$ | async) ?? [];
@let statuses = (agentsService.statuses$ | async) ?? [];
@let isAgentsLoading = agentsService.agentsLoadingStatus() === LoadingState.LOADING;
@let isAgentsLoaded = agentsService.agentsLoadingStatus() === LoadingState.READY;
@let isApproveAgentLoading = agentsService.agentApproveLoadingStatus() === LoadingState.LOADING;
@let isAgentStatusesLoading = agentsService.agentStatusesLoadingStatus() === LoadingState.LOADING;

<section class="agents page-viewport">
  <mon-page-toolbar
    titleTranslateKey="monitoring.menu.agents"
    [searchControl]="searchControl"
    searchPlaceholderTranslateKey="monitoring.search"
    [selectedFiltersCount]="selectedFiltersCount"
  >
    <form
      toolbarFilters
      class="agents__filters"
      [formGroup]="filterForm"
    >
      @if (canReadFacilities()) {
        <mon-facility-selector
          class="agents__filter"
          [controlEntity]="facilityIdsControl"
          (selectionChange)="onFacilitiesSelectionChange($event)"
        />
      }

      <lib-multiselect
        titleTranslationKey="monitoring.agents.filters.status"
        chosenTranslationKey="monitoring.agents.filters.status"
        clearAllTranslationKey="monitoring.clear"
        [options]="statuses"
        formControlName="statusIds"
        [libSkeleton]="isAgentStatusesLoading"
      />

      <lib-select
        class="agents__filter"
        formControlName="approval"
        [options]="(approvalOptions$ | async) ?? []"
        labelTranslateKey="monitoring.agents.filters.approval.label"
        [isClearable]="true"
        [fullWidth]="true"
      />
    </form>

    @if (isFiltersActive) {
      <div
        toolbarSelectedFilters
        class="agents__selected-filters"
      >
        <lib-button
          class="chip chip--button"
          buttonStyle="danger"
          buttonSize="medium"
          type="button"
          (click)="clearAllFilters()"
        >
          <span>{{ 'monitoring.agents.filters.clear-filters' | translate }}</span>
        </lib-button>

        @if (canReadFacilities()) {
          @for (facility of selectedFacilities; track facility.id) {
            <lib-button
              class="chip chip--button"
              buttonStyle="danger"
              buttonSize="medium"
              type="button"
              (click)="removeFacilityFilter(facility.id)"
            >
              <lib-svg-icon
                icon="close"
                [size]="16"
              />
              <span>{{ facility.name }}</span>
            </lib-button>
          }
        }

        @for (status of selectedStatuses; track status.id) {
          <lib-button
            class="chip chip--button"
            buttonStyle="danger"
            buttonSize="medium"
            type="button"
            (click)="removeStatusFilter(status.id)"
          >
            <lib-svg-icon
              icon="close"
              [size]="16"
            />
            <span>{{ status.name }}</span>
          </lib-button>
        }

        @if (filterForm.controls.approval.value) {
          <lib-button
            class="chip chip--button"
            buttonStyle="danger"
            buttonSize="medium"
            type="button"
            (click)="removeApprovalFilter()"
          >
            <lib-svg-icon
              icon="close"
              [size]="16"
            />
            <span>{{ filterForm.controls.approval.value.label }}</span>
          </lib-button>
        }
      </div>
    }
  </mon-page-toolbar>

  <section class="page-content page-table page-table-scroll">
    <div class="page-table-scroll__content">
      <lib-table
        class="page-table-scroll__table agents__table"
        [columns]="AGENTS_TABLE_COLUMNS"
        [dataSource]="agents"
        [cellTemplates]="{
          name: nameTpl,
          facilityName: facilityNameTpl,
          statusName: statusTpl,
          comment: commentTpl,
          actions: actionsTpl,
        }"
        [loaded]="isAgentsLoaded"
        [loading]="isAgentsLoading"
      />
    </div>

    @if (!(agents.length === 0 && currentPage() === 1)) {
      <lib-pagination
        class="agents__pagination"
        [totalCount]="agentsService.totalCount()"
        [pageIndex]="currentPage()"
        [pageSize]="100"
        [loading]="isAgentsLoading"
        (paginationChange)="onPageChange($event)"
      />
    }
  </section>

  <ng-template
    #nameTpl
    let-row="data"
  >
    @let data = asAgent(row);
    <div class="agents__name">
      <div>{{ data.name }}</div>
      @if (!data.isApproved) {
        <div class="agents__badge agents__badge--error">
          {{ 'monitoring.agents.table.badges.unapproved' | translate }}
        </div>
      }
    </div>
  </ng-template>

  <ng-template
    #facilityNameTpl
    let-row="data"
  >
    {{ asAgent(row).facilityName ?? ('monitoring.common.no-data' | translate) }}
  </ng-template>

  <ng-template
    #statusTpl
    let-row="data"
  >
    <div
      class="agents__status"
      [class]="'agents__status--' + getStatusLabel(asAgent(row).status)"
    >
      {{ 'monitoring.agents.statuses.' + getStatusLabel(asAgent(row).status) | translate }}
    </div>
  </ng-template>

  <ng-template
    #commentTpl
    let-row="data"
  >
    {{ asAgent(row).comment ?? ('monitoring.common.no-data' | translate) }}
  </ng-template>

  <ng-template
    #actionsTpl
    let-row="data"
  >
    @let data = asAgent(row);
    <div class="agents__actions">
      @if (data.isApproved) {
        @if (canReadScans()) {
          <lib-icon-button
            icon="scanner"
            (click)="navigateToScanner(data)"
            [title]="'monitoring.agents.actions.scanner' | translate"
          />
        }
        @if (canEditAgentFacility) {
          <lib-icon-button
            icon="pen"
            (click)="openAgentModal(data.id, data.name, ModalAction.EDIT)"
            [title]="'monitoring.agents.actions.edit' | translate"
          />
        }
        @if (canManageAgents()) {
          <lib-icon-button
            icon="trash"
            (click)="confirmDeleteModal(data)"
            [title]="'monitoring.agents.actions.delete' | translate"
          />
        }
      } @else {
        @if (canEditAgentFacility) {
          <lib-button
            class="agents__actions-approve"
            buttonSize="medium"
            buttonStyle="transparent-tertiary"
            (click)="openAgentModal(data.id, data.name, ModalAction.APPROVE)"
            [disabled]="isApproveAgentLoading"
          >
            <lib-svg-icon icon="check-square" />
            {{ 'monitoring.agents.actions.approve' | translate }}
          </lib-button>
        }
      }
    </div>
  </ng-template>
</section>
